// CTV-682: shared chrome + stylesheet for every printable material.
//
// The sheet is deliberately plain CSS in a <style> block rather than Tailwind:
// it has to survive `window.print()` at exact letter dimensions, and the print
// rules (fixed inch sizing, colour-adjust, hiding the admin chrome around it)
// don't express well as utility classes. Keeping it in one place also means a
// new material inherits correct print behaviour for free.
//
// Screen sizing is 850 × 1100px, which is letter *proportion*. Print re-sizes
// in real inches — 1100px is 11.46in at 96dpi and would otherwise spill onto a
// second page.

import React from 'react';
import type { PublisherConfig } from '../../../types/selfService';
import { displayedName } from '../../../data/publisherConfigs';
import { sheetPalette } from './sheetColor';

export type SheetVariant = 'brochure' | 'memo' | 'poster';

interface MaterialSheetProps {
  config: PublisherConfig;
  /** Small right-aligned label in the sheet header. */
  kicker: React.ReactNode;
  /** Left-hand footer line, typically a copyright. */
  footNote: string;
  /**
   * 'brochure' = rounded cards (advertiser-facing).
   * 'memo'     = ruled, squared (internal).
   * 'poster'   = CTV-682 rev 3: hero with the TV, filled accent blocks.
   */
  variant: SheetVariant;
  children: React.ReactNode;
}

const MaterialSheet: React.FC<MaterialSheetProps> = ({
  config,
  kicker,
  footNote,
  variant,
  children,
}) => {
  const palette = sheetPalette(config);
  const name = displayedName(config);

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        className={`msheet msheet--${variant}`}
        style={
          {
            // A ladder, not one tone. `ink` is the only rung safe as text on
            // paper; `deep` and `mid` are both safe under white text, and
            // being two steps apart is what stops the sheet reading flat.
            '--msheet-accent': palette.accent,
            '--msheet-accent-ink': palette.ink,
            '--msheet-accent-deep': palette.deep,
            '--msheet-accent-mid': palette.mid,
            '--msheet-accent-soft': palette.soft,
            '--msheet-accent-wash': palette.wash,
            '--msheet-accent-edge': palette.edge,
          } as React.CSSProperties
        }
      >
        <div className="msheet-head">
          {config.logoUrl ? (
            <img
              className={`msheet-logo${config.logoInvert ? ' msheet-logo--invert' : ''}`}
              src={config.logoUrl}
              alt={name}
              style={{ transform: `scale(${config.logoScale ?? 1})` }}
            />
          ) : (
            // Station hasn't uploaded a logo yet. A labelled placeholder beats a
            // blank corner, and makes the gap obvious in the preview.
            <div className="msheet-logo-empty">{name}</div>
          )}
          <div className="msheet-kicker">{kicker}</div>
        </div>

        {children}

        <div className="msheet-foot">
          <div className="msheet-footnote">{footNote}</div>
          <div className="msheet-pw">
            <span>Powered by</span>
            <img src={`${import.meta.env.BASE_URL}ctvbuyer-logo-email.png`} alt="CTVBuyer" />
          </div>
        </div>
      </div>
    </>
  );
};

const SHEET_CSS = `
.msheet {
  --msheet-ink: #0f172a;
  --msheet-muted: #57534e;
  --msheet-faint: #a8a29e;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Inter, sans-serif;
  color: var(--msheet-ink);
  width: 850px; min-height: 1100px;
  margin: 0 auto; padding: 52px 60px 0;
  display: flex; flex-direction: column;
  /* Reads as a sheet of paper on a desk: hairline edge + lifted shadow. The
     sheet's own background is a warm off-white, so without these it blends
     into the admin page and you can't tell where the document starts. */
  border: 1px solid rgba(15, 23, 42, .10);
  box-shadow: 0 1px 2px rgba(15,23,42,.06), 0 18px 44px -12px rgba(15,23,42,.28);
  -webkit-font-smoothing: antialiased;
}
.msheet--brochure { background: #faf9f7; border-radius: 6px; }
.msheet--memo { background: #f7f7f8; border-radius: 0; }
.msheet--poster { background: #faf9f7; border-radius: 6px; }
/* The poster's hero carries its own weight, so the header sits closer to it
   than on the other variants (54px). */
.msheet--poster .msheet-head { margin-bottom: 22px; }
/* Gap between the budget pill and the step cards. */
.msheet--poster .msheet-mid { margin-top: 22px; gap: 18px; }

.msheet * { box-sizing: border-box; margin: 0; padding: 0; }

/* header */
.msheet-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 54px; }
.msheet-logo { height: 54px; width: auto; object-fit: contain; transform-origin: left center; }
.msheet-logo--invert { filter: brightness(0) invert(1); }
.msheet-logo-empty {
  height: 54px; display: flex; align-items: center; padding: 0 18px;
  border: 1px dashed #cfcbc4; border-radius: 4px;
  font-size: 12px; font-weight: 700; letter-spacing: .04em; color: var(--msheet-faint);
  text-transform: uppercase;
}
.msheet-kicker {
  text-align: right; font-size: 10px; font-weight: 700;
  letter-spacing: .16em; text-transform: uppercase; color: var(--msheet-faint); line-height: 1.7;
}
.msheet-tag {
  display: inline-block; margin-bottom: 7px; padding: 4px 10px;
  border: 1px solid #3f3f46; font-size: 10px; font-weight: 700;
  letter-spacing: .18em; text-transform: uppercase; color: #3f3f46;
}

/* hero */
.msheet-eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: .16em;
  text-transform: uppercase; color: var(--msheet-accent-ink); margin-bottom: 18px;
}
.msheet h1 { font-size: 47px; line-height: 1.1; letter-spacing: -.03em; font-weight: 700; margin-bottom: 20px; }
/* Two deliberate lines: never let the browser pick the break. */
.msheet h1 span, .msheet h1 em { display: block; white-space: nowrap; }
.msheet h1 em {
  font-style: italic; font-weight: 600; color: var(--msheet-accent-ink);
  font-family: Georgia, "Times New Roman", serif;
}
.msheet-sub { font-size: 19px; line-height: 1.5; color: var(--msheet-muted); font-weight: 450; }
.msheet-sub b { font-weight: 700; color: #292524; }

/* Steps, why, chips, and CTA share the leftover vertical space so every gap
   between them is computed the same way and stays equal. */
.msheet-mid {
  flex: 1; display: flex; flex-direction: column;
  justify-content: space-between; gap: 26px; margin: 44px 0 0;
}

/* Step layout is independent of the paper variant: three-abreast cards suit a
   3-step story, ruled rows suit a longer walkthrough. Either can appear on
   either paper. */
.msheet-steps--cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.msheet-steps--cards .msheet-step {
  background: #fff; border: 1px solid #e8e5e0; border-radius: 12px; padding: 26px 24px 30px;
}
.msheet--memo .msheet-steps--cards .msheet-step { border-radius: 3px; border-color: #d9d9dd; }
.msheet-steps--cards .msheet-num { font-size: 40px; line-height: 1; margin-bottom: 18px; }

.msheet-steps--rows { border-top: 1px solid #d9d9dd; }
.msheet-steps--rows .msheet-step {
  display: grid; grid-template-columns: 46px 1fr; align-items: baseline;
  gap: 0 18px; padding: 19px 2px; border-bottom: 1px solid #d9d9dd;
}
/* Both text nodes share column 2; without this the <p> wraps into the numeral
   column and every row balloons. */
.msheet-steps--rows .msheet-step h3,
.msheet-steps--rows .msheet-step p,
.msheet-steps--rows .msheet-step ul { grid-column: 2; }
.msheet-steps--rows .msheet-num { font-size: 26px; line-height: 1; }

.msheet-num { font-family: Georgia, "Times New Roman", serif; font-weight: 700; color: var(--msheet-accent-ink); }
.msheet-step h3 { font-size: 18px; letter-spacing: -.02em; font-weight: 700; margin-bottom: 6px; }
.msheet-step p { font-size: 15px; line-height: 1.55; color: var(--msheet-muted); }
.msheet-step p b { font-weight: 700; color: #292524; }
/* Bulleted detail. Custom marker rather than list-style so the dot aligns to
   the first line's cap height regardless of body size. */
.msheet-step ul { list-style: none; }
.msheet-step li {
  position: relative; padding-left: 14px;
  font-size: 15px; line-height: 1.55; color: var(--msheet-muted);
}
.msheet-step li + li { margin-top: 4px; }
.msheet-step li::before {
  content: ''; position: absolute; left: 2px; top: .58em;
  width: 4px; height: 4px; border-radius: 50%; background: var(--msheet-faint);
}
.msheet-step li b { font-weight: 700; color: #292524; }
/* A five-row walkthrough carries far more copy per row than a three-step
   story, so the dense modifier sets its body text smaller and tighter. */
.msheet-steps--dense .msheet-step p,
.msheet-steps--dense .msheet-step li { font-size: 13.5px; line-height: 1.5; }
.msheet-steps--dense .msheet-step { padding: 13px 2px; }
.msheet-steps--dense .msheet-step h3 { font-size: 16.5px; margin-bottom: 4px; }

/* why pair */
.msheet-why { display: grid; grid-template-columns: 1fr 1fr; }
.msheet--brochure .msheet-why { gap: 16px; }
.msheet--brochure .msheet-why > div {
  background: #fff; border: 1px solid #e8e5e0; border-radius: 12px; padding: 24px 24px 26px;
}
.msheet--memo .msheet-why { gap: 34px; }
.msheet--memo .msheet-why > div { border-top: 2px solid #3f3f46; padding: 15px 0 0; }
.msheet-why-label {
  font-size: 10px; font-weight: 700; letter-spacing: .14em;
  text-transform: uppercase; color: var(--msheet-accent-ink); margin-bottom: 12px;
}
.msheet-why h4 { font-size: 18px; letter-spacing: -.02em; font-weight: 700; margin-bottom: 8px; }
.msheet-why p { font-size: 14.5px; line-height: 1.55; color: var(--msheet-muted); }
/* Compact modifier for sheets whose steps already carry the detail — the pair
   becomes a short "and then this happens" note rather than a second feature
   block. Used by the getting-started walkthrough, which has five steps and no
   room for full-size cards. */
.msheet-why--compact .msheet-why-label { margin-bottom: 4px; }
.msheet-why--compact h4 { font-size: 15px; margin-bottom: 2px; }
.msheet-why--compact p { font-size: 13px; line-height: 1.45; }
.msheet--brochure .msheet-why--compact > div { padding: 12px 14px 13px; }

/* Tight closing panel: same layout, less air and a smaller code. The QR still
   clears the ~0.6in floor for a phone scan at arm's length. */
.msheet-cta--tight { padding: 11px 14px; }
.msheet-cta--tight .msheet-cta-qr { padding: 5px; }
.msheet-cta--tight .msheet-cta-qr svg { width: 62px; height: 62px; }
.msheet-cta--tight strong { font-size: 19px; }
.msheet-cta--tight span { font-size: 17px; }

/* chips */
.msheet-chips { display: flex; flex-wrap: wrap; gap: 9px; }
.msheet-chip {
  font-size: 13px; font-weight: 550; color: #44403c;
  background: #fff; border: 1px solid #e0ddd7; padding: 8px 15px;
}
.msheet--brochure .msheet-chip { border-radius: 999px; }
.msheet--memo .msheet-chip { border-radius: 3px; border-color: #d9d9dd; }
.msheet-chip--hi { border-color: var(--msheet-accent-ink); color: var(--msheet-accent-ink); font-weight: 700; }

/* Closing panel. A printed sheet has no button to press, so the payload is the
   URL — but a bare rule read as footer chrome and got skipped. A wash of the
   station accent makes it a destination without the heavy black bar it
   replaced: still far less ink, still ~40px shorter, but unmissable.
   Identical on every variant except the corner radius, which follows the
   paper. */
.msheet-cta {
  background: var(--msheet-accent-wash);
  border: 1px solid var(--msheet-accent-edge);
  padding: 16px 20px;
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
/* Label and copy stack in one column so the QR can span both rows. Previously
   the label was a full-width flex row, which meant the code only ever spanned
   the copy row and pushed the panel ~23px taller for no reason. */
.msheet-cta-text { display: flex; flex-direction: column; min-width: 0; }
.msheet--brochure .msheet-cta { border-radius: 12px; }
.msheet--memo .msheet-cta { border-radius: 3px; }
.msheet-cta-label {
  font-size: 10px; font-weight: 700; letter-spacing: .16em;
  text-transform: uppercase; color: var(--msheet-accent-ink);
  margin-bottom: 2px;
}
.msheet-cta-main { display: flex; flex-direction: column; gap: 3px; }
.msheet-cta strong { font-size: 22px; letter-spacing: -.022em; font-weight: 700; }
.msheet-cta span,
.msheet-cta a {
  font-size: 19px; font-weight: 700; letter-spacing: -.01em;
  color: var(--msheet-accent-ink);
}
/* The URL is an anchor so it survives Save-as-PDF as a real, clickable link
   annotation — a <span> exports as inert text. Styled to look identical: no
   underline, no browser blue. */
.msheet-cta a { text-decoration: none; }
/* A QR needs a white quiet zone to scan — it cannot sit on the accent wash.
   The tile provides that margin and keeps the code legible on any station
   colour. line-height:0 stops the inline SVG adding descender space. */
.msheet-cta-qr {
  background: #fff; padding: 6px; border-radius: 6px; line-height: 0;
  border: 1px solid var(--msheet-accent-edge); flex-shrink: 0;
}

/* =====================================================================
   POSTER (rev 3). Hero pairs the headline with the landing page's TV
   chrome; the station accent fills the blocks the draft drew in navy.
   Every filled block uses --msheet-accent-ink, not the raw accent: it's
   derived to clear AA both as text on paper and under white text on a
   fill, so reversed copy holds in colour and in greyscale.
   ===================================================================== */
.msheet--poster .msheet-hero { display: grid; grid-template-columns: 1fr 350px; gap: 30px; align-items: start; }
/* The TV asset isn't symmetrical — the stand and remote sit right of the
   screen, so the screen's optical centre lands left of the column's centre.
   Nudge the whole right column (TV + caption together) so the screen reads
   as centred against the headline. */
.msheet--poster .msheet-hero > div:last-child { transform: translateX(24px); }
.msheet--poster h1 {
  /* 42px: the text column is 350px (850 sheet - 120 padding - 350 TV - 30
     gap). "Get your business" measures 365px at 46 and 349px at 44 — the
     latter fits by a single pixel, which isn't enough to reliably keep
     "business" on line one. 42px leaves 16px. */
  font-size: 42px; line-height: 1.04; letter-spacing: -.034em; font-weight: 800; margin: 10px 0 14px;
}
/* Poster headline wraps freely — it's a paragraph-shaped block, not the
   two locked lines the other variants use. */
.msheet--poster h1 span, .msheet--poster h1 em { display: inline; white-space: normal; }
.msheet--poster h1 em { font-style: normal; font-weight: 800; color: var(--msheet-accent-ink); font-family: inherit; }
.msheet--poster .msheet-sub { font-size: 16.5px; line-height: 1.5; }

/* Label centres against the number rather than sitting on its baseline: the
   number is nearly 3x the label's size, so baseline alignment reads as the
   label having slipped to the bottom. The $ is positioned explicitly instead
   of vertical-align:super, which shifts against a baseline that moves with
   the font size. */
.msheet-pill {
  display: inline-flex; align-items: center; gap: 9px; margin-top: 18px;
  background: var(--msheet-accent-deep); color: #fff; padding: 12px 20px; border-radius: 10px;
}
.msheet-pill span {
  font-size: 11px; font-weight: 700; letter-spacing: .14em;
  text-transform: uppercase; opacity: .85; line-height: 1;
}
.msheet-pill b {
  display: inline-flex; align-items: baseline; gap: 1px;
  font-size: 30px; font-weight: 800; letter-spacing: -.02em; line-height: 1;
}
.msheet-pill b i {
  font-style: normal; font-size: 16px; font-weight: 700;
  position: relative; top: -7px;
}

/* TV: the landing page's own chrome asset. Insets are pure percentages so the
   screen rectangle tracks the chrome at any width, and match
   SelfServiceLanding exactly.
   The fill paints OVER the image, so a sliver of fill under a dark bezel is
   invisible while a sliver of paper is not — err tight, not loose. */
.msheet-tv { position: relative; width: 100%; }
/* The chrome sits ABOVE the screen fill. Its screen area is transparent, so
   the fill shows through the opening while the bezel masks any overshoot.
   That's what makes the alignment robust: painting the fill on top meant
   overshoot covered the bezel and undershoot showed paper, so it had to land
   pixel-exact — and screen and print round the pixel grid differently, so it
   could only ever be right in one of them. */
.msheet-tv > img { display: block; width: 100%; height: auto; position: relative; z-index: 1; }
.msheet-tv-screen {
  /* Deliberately overshot past the opening — the bezel hides it. Do not
     "correct" these back to the landing page's values; the hairline they
     leave is exactly what this avoids. */
  position: absolute; top: 0.30%; left: 1.55%; right: 9.85%; bottom: 15.95%;
  z-index: 0;
  /* A screen glows, so it gets a gradient where the rest of the sheet is
     flat. Built from the ladder's own rungs, so both ends already hold white
     text in colour and in greyscale. 135deg matches SelfServiceLanding's fill
     of this same rectangle, so the sheet's TV and the site's read alike. */
  overflow: hidden;
  background: linear-gradient(135deg, var(--msheet-accent-mid), var(--msheet-accent-deep));
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  /* bottom padding clears the transport bar so the centred copy isn't
     optically pushed down by it */
  gap: 4px; color: #fff; text-align: center; padding: 0 8% 26px;
}

/* Transport bar, as in the draft: play glyph, progress with a knob, duration.
   Decorative — it says "this is a real spot", nothing more. */
.msheet-tv-bar {
  position: absolute; left: 7%; right: 7%; bottom: 9%;
  display: flex; align-items: center; gap: 8px; color: #fff;
}
.msheet-tv-bar svg { width: 11px; height: 11px; flex-shrink: 0; }
.msheet-tv-track {
  position: relative; flex: 1; height: 2px; border-radius: 2px;
  background: rgba(255,255,255,.38);
}
.msheet-tv-track::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 46%;
  background: #fff; border-radius: 2px;
}
.msheet-tv-track::after {
  content: ''; position: absolute; left: 46%; top: 50%;
  transform: translate(-50%, -50%);
  width: 7px; height: 7px; border-radius: 50%; background: #fff;
}
.msheet-tv-time { font-size: 9px; font-weight: 700; letter-spacing: .02em; flex-shrink: 0; }
.msheet-tv-screen strong { font-size: 28px; font-weight: 800; letter-spacing: -.018em; }
.msheet-tv-screen em { font-style: normal; font-size: 14.5px; opacity: .9; }
.msheet-tv-live {
  position: absolute; top: 7%; left: 7%; display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255,255,255,.18); padding: 3px 8px; border-radius: 999px;
  font-size: 8.5px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #fff;
}
.msheet-tv-live::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: #fff; }
/* Centred on the SCREEN, not the image box. The screen sits between the 1.89%
   and 89.9% insets, so its midpoint is 45.9% — the stand and remote on the
   right push the box's own centre 4.1% (about 14px here) to the right of it. */
.msheet-tv-cap {
  text-align: center; margin-top: 10px; transform: translateX(-14px);
  font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--msheet-faint);
}
.msheet-tv-cap b { display: block; margin-top: 3px; font-size: 12.5px; letter-spacing: .02em; color: var(--msheet-muted); text-transform: none; }

.msheet--poster .msheet-steps--cards { gap: 14px; }
/* Soft tint rather than white: white cards on off-white paper read as one
   flat field, which is what made the sheet feel colourless. */
.msheet--poster .msheet-step {
  background: var(--msheet-accent-soft); border: 1px solid var(--msheet-accent-edge);
  border-radius: 12px; padding: 12px 14px 14px;
}
/* All three card headings fit one line at this size (169/167/93px against a
   202px card), so no height is reserved — a min-height would show as dead
   space under every heading. Re-check if a heading grows. */
.msheet--poster .msheet-step h3 { font-size: 16px; margin: 9px 0 4px; }
.msheet--poster .msheet-step p { font-size: 13.5px; line-height: 1.45; }
.msheet-step-num {
  width: 26px; height: 26px; border-radius: 7px; background: var(--msheet-accent-mid);
  color: #fff; display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800;
}
.msheet-step-tag {
  display: inline-block; margin-top: 10px; background: var(--msheet-accent-wash);
  color: var(--msheet-accent-ink); font-size: 11px; font-weight: 700;
  padding: 4px 9px; border-radius: 6px;
}

/* The draft's two navy cards. Reversed out of the accent tone. */
.msheet--poster .msheet-why { gap: 14px; }
.msheet--poster .msheet-why > div {
  background: var(--msheet-accent-deep); color: #fff; border: 0; border-radius: 12px; padding: 16px 18px 18px;
}
.msheet--poster .msheet-why-label { color: rgba(255,255,255,.8); margin-bottom: 5px; }
.msheet--poster .msheet-why h4 { font-size: 17px; margin-bottom: 5px; }
.msheet--poster .msheet-why p { color: rgba(255,255,255,.88); font-size: 13.5px; line-height: 1.45; }

/* Spread across the full width, but each chip sized to its own text.
   Equal columns forced a long locality label ("Local to Cedar Rapids-
   Waterloo" is 193px against a 175px column) onto a second line, which cost
   14px and tipped that station's sheet onto a second page. */
.msheet--poster .msheet-chips {
  display: flex; justify-content: space-between; gap: 10px; flex-wrap: nowrap;
}
.msheet--poster .msheet-chip { white-space: nowrap; }
.msheet--poster .msheet-chip {
  border-radius: 9px; font-size: 12.5px; padding: 9px 10px;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  text-align: center;
}
.msheet-chip-icon { width: 14px; height: 14px; color: var(--msheet-accent-ink); flex-shrink: 0; }
.msheet-step-head { display: flex; align-items: center; justify-content: space-between; }
.msheet-step-icon { width: 17px; height: 17px; color: var(--msheet-accent-ink); opacity: .85; }
.msheet--poster .msheet-why-label { display: flex; align-items: center; gap: 6px; }
.msheet-why-icon { width: 14px; height: 14px; flex-shrink: 0; }

.msheet--poster .msheet-cta {
  /* Left to right, deep under the copy and the URL button, lightening toward
     the QR's white tile at the far end. Both stops are ladder rungs, so the
     reversed text clears contrast anywhere along it. */
  background: linear-gradient(90deg, var(--msheet-accent-deep), var(--msheet-accent-mid));
  border: 0; border-radius: 12px; padding: 18px 22px;
}
.msheet--poster .msheet-cta-label { color: rgba(255,255,255,.82); }
.msheet--poster .msheet-cta strong { color: #fff; font-size: 25px; }
/* The arrow is decoration: in the markup it would land inside the link text
   and become part of the accessible name. */
.msheet--poster .msheet-cta a::after { content: ' →'; }
.msheet--poster .msheet-cta a {
  display: inline-block; margin-top: 9px; background: rgba(255,255,255,.14);
  border: 1px solid rgba(255,255,255,.34); color: #fff; font-size: 19px;
  font-weight: 700; padding: 10px 19px; border-radius: 9px;
}
.msheet--poster .msheet-cta-qr { border-color: rgba(255,255,255,.5); }

/* footer */
.msheet-foot {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 0 26px; font-size: 11px; color: var(--msheet-faint);
}
.msheet-pw { font-weight: 600; color: #78716c; display: inline-flex; align-items: center; gap: 7px; }
/* The wordmark's optical centre sits below its box centre (tall antennae, low
   descenders), so the correction goes on the logo, leaving both text runs
   flex-centred and therefore always aligned with each other. */
.msheet-pw img { height: 26px; width: auto; opacity: .9; position: relative; top: -4px; }

@media print {
  @page { size: letter portrait; margin: 0; }
  html, body {
    background: #fff !important; margin: 0 !important; padding: 0 !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  /* Print only the sheet.
     display:none, NOT visibility:hidden — a hidden element keeps its box, so
     the admin shell's full height still paginated and trailed blank pages
     after the sheet. Anything that neither contains the sheet nor is part of
     it is removed from layout entirely. */
  *:not(:has(.msheet)):not(.msheet):not(.msheet *) { display: none !important; }
  /* Flatten the surviving ancestor chain (page padding, flex columns, the
     scroll container) so the sheet starts at the page origin instead of
     inheriting admin-shell geometry. */
  :has(.msheet) {
    display: block !important;
    margin: 0 !important; padding: 0 !important; border: 0 !important;
    width: auto !important; max-width: none !important;
    height: auto !important; min-height: 0 !important; max-height: none !important;
    overflow: visible !important; background: #fff !important; box-shadow: none !important;
  }
  .msheet {
    width: 8.5in !important; height: 10.94in !important; min-height: 0 !important;
    margin: 0 !important; padding: 0.42in 0.55in 0 !important;
    box-shadow: none !important; border: 0 !important; border-radius: 0 !important;
    break-inside: avoid; page-break-inside: avoid;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  /* Both variants need print tightening to clear the page box; screen keeps
     the roomier values. Sizes here are the *fixed* contributors to height —
     .msheet-mid redistributes whatever slack is left, so trimming a gap floor
     costs nothing visually but buys headroom against overflow.
     Brochure was overflowing by about the height of its footer. */
  .msheet--brochure .msheet-head { margin-bottom: 34px; }
  .msheet--brochure h1 { font-size: 42px; margin-bottom: 14px; }
  /* The five-step walkthrough carries more copy than the 3-step sheets, so it
     gives back a little headline and header air to clear the page box. */
  .msheet--brochure:has(.msheet-steps--dense) .msheet-head { margin-bottom: 22px; }
  .msheet--brochure:has(.msheet-steps--dense) h1 { font-size: 36px; margin-bottom: 10px; }
  .msheet--brochure:has(.msheet-steps--dense) .msheet-sub { font-size: 17px; }
  .msheet--brochure:has(.msheet-steps--dense) .msheet-mid { margin-top: 16px; gap: 8px; }
  .msheet--brochure:has(.msheet-steps--dense) .msheet-foot { padding: 8px 0 12px; }
  .msheet--brochure .msheet-mid { margin-top: 28px; gap: 18px; }
  .msheet-steps--cards .msheet-step { padding: 20px 22px 22px; }
  .msheet--brochure .msheet-why > div { padding: 18px 20px 20px; }
  .msheet--brochure .msheet-cta { padding-top: 12px; }
  .msheet--brochure .msheet-foot { padding: 12px 0 16px; }

  /* The memo layout runs taller than the brochure (three stacked ruled rows),
     so it needs its own tightening to land on one page. */
  .msheet--memo .msheet-head { margin-bottom: 20px; }
  .msheet--memo h1 { font-size: 40px; margin-bottom: 14px; }
  .msheet--memo .msheet-mid { margin-top: 20px; gap: 10px; }
  .msheet-steps--rows .msheet-step { padding: 11px 2px; }
  .msheet-steps--dense .msheet-step { padding: 6px 2px; }
  .msheet-steps--dense .msheet-step li { font-size: 12.5px; line-height: 1.45; }
  /* These must come after the variant rules above: same specificity, so the
     later declaration wins and the compact modifiers would otherwise be
     silently overridden by the generic print padding. */
  .msheet--brochure .msheet-why--compact > div { padding: 10px 12px 11px; }
  .msheet-cta--tight { padding: 9px 12px; }
  .msheet-cta--tight .msheet-cta-label { margin-bottom: 0; }
  .msheet--memo .msheet-cta { padding-top: 12px; }

  /* Poster: print-only tightening. Screen keeps the airier spacing; these
     close the last 22px against the page box.
     CTV-810 (2026-08-31): the device list is dynamic now, and the longest
     set ("Roku, Samsung TV, Android TV, Fire TV, Apple TV, and Desktop")
     adds a subhead line and wraps the TV caption, which pushed the footer
     onto page 2. The h1/sub/margin trims below reclaim that headroom;
     measured worst case leaves ~44px of slack inside the page box. */
  .msheet--poster h1 { font-size: 40px; margin-bottom: 12px; }
  .msheet--poster .msheet-sub { font-size: 16px; }
  .msheet--poster .msheet-head { margin-bottom: 12px; }
  .msheet--poster .msheet-mid { margin-top: 12px; gap: 9px; }
  .msheet--poster .msheet-steps--cards { gap: 12px; }
  .msheet--poster .msheet-why { gap: 12px; }
  .msheet--poster .msheet-cta { padding: 13px 20px; }
  .msheet--poster .msheet-foot { padding: 8px 0 10px; }
  .msheet--memo .msheet-foot { padding: 12px 0 16px; }
}
`;

export default MaterialSheet;
